/**
 * <mab-reveal>
 *
 * A scroll reveal for tagged descendants. Deliberately a plain custom element
 * with no imports from @theme/utilities or any other theme module, the same way
 * the locket upload element is built: this stays out of the theme's module graph
 * so a theme upgrade cannot change its behaviour.
 *
 * It is a custom element rather than a plain script because connectedCallback
 * fires again when the theme editor re-renders a section. That covers re-init
 * without listening for shopify:section:load or shopify:section:select.
 *
 * The mechanism here is generic; the scope is not. Only the drop section carries
 * the CSS that gives [data-mab-reveal] a hidden starting state, so this element
 * reveals nothing anywhere else even if it is placed there.
 *
 * Contract with the CSS:
 * - Tagged elements are VISIBLE by default. The hidden starting state applies
 *   only under .mab-reveal--armed, which this file adds. If the script never
 *   runs, is blocked, or throws, the section renders fully visible.
 * - The class is added synchronously in connectedCallback, before any paint that
 *   could show and then hide the content.
 * - Per-element stagger is published as --mab-reveal-delay. No nth-child delays
 *   in CSS, so the count of cards is free to change.
 *
 * Reduced motion is checked here and, as a second line of defence, again in CSS.
 * If the preference is set we return early and never arm, so nothing is ever
 * hidden even for the instant before the observer fires.
 */

const REVEAL_SELECTOR = '[data-mab-reveal]';
const ARMED_CLASS = 'mab-reveal--armed';
const VISIBLE_ATTR = 'data-mab-reveal-visible';

/** Heading unit leads, then the cards follow it in. Milliseconds. */
const HEADING_DELAY = 0;
const CARD_FIRST_DELAY = 120;
const CARD_STEP_DELAY = 80;

const THRESHOLD = 0.15;

class MabReveal extends HTMLElement {
  /** @type {IntersectionObserver | null} */
  #observer = null;

  connectedCallback() {
    // Never hide anything for a reader who asked for less motion.
    if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return;

    // No observer means no way to know when to reveal, so leave it all visible.
    if (typeof IntersectionObserver === 'undefined') return;

    const targets = this.querySelectorAll(REVEAL_SELECTOR);

    // Nothing tagged yet. This also covers an upgrade that lands before the
    // children are parsed: arming an empty host would hide content that arrives
    // afterwards with no observer watching it.
    if (targets.length === 0) return;

    // Synchronous, so the hidden state is in place before the first paint of
    // this section rather than after it.
    this.classList.add(ARMED_CLASS);

    this.#assignDelays(targets);

    this.#observer = new IntersectionObserver(
      (entries, observer) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          entry.target.setAttribute(VISIBLE_ATTR, '');
          // Fire once. A revealed element is done with us.
          observer.unobserve(entry.target);
        }
      },
      { threshold: THRESHOLD }
    );

    for (const target of targets) this.#observer.observe(target);
  }

  disconnectedCallback() {
    this.#observer?.disconnect();
    this.#observer = null;
  }

  /**
   * The stagger is keyed off the attribute value, not DOM order, so inserting
   * anything between the heading and the grid cannot silently reshuffle it.
   *
   * @param {NodeListOf<Element>} targets
   */
  #assignDelays(targets) {
    let cardIndex = 0;

    for (const target of targets) {
      const kind = target.getAttribute('data-mab-reveal');
      let delay = HEADING_DELAY;

      if (kind === 'card') {
        delay = CARD_FIRST_DELAY + cardIndex * CARD_STEP_DELAY;
        cardIndex += 1;
      }

      /** @type {HTMLElement} */ (target).style.setProperty('--mab-reveal-delay', `${delay}ms`);
    }
  }
}

if (!customElements.get('mab-reveal')) {
  customElements.define('mab-reveal', MabReveal);
}
